import {
  TIntegrationSchemaDiscoveryResponse,
  TIntegrationTypeMetadata,
} from "../../../../../services/api-integrations";
import { TIntegrationCatalogRow, TIntegrationV2Status } from "../types";

const READY_PREFIXES = ["pipedrive_"];
const HIDDEN_INTEGRATIONS = new Set([
  "airtable_top_aesthetics",
  "airtable_top_aesthetics_main",
  "gohighlevel_v2",
]);

const inferStatus = (
  metadata: TIntegrationTypeMetadata,
): TIntegrationV2Status => {
  const typeLower = metadata.integration_type.toLowerCase();

  if (!metadata.enabled) return "coming_soon";
  if (typeLower === "drchrono") return "coming_soon";
  if (typeLower === "4demr") return "ready";
  if (typeLower === "meta_pixel") return "ready";
  if (typeLower.includes("gohighlevel")) return "coming_soon";
  if (typeLower === "webhook") return "ready";
  if (READY_PREFIXES.some((prefix) => typeLower.startsWith(prefix))) {
    return "ready";
  }

  return "coming_soon";
};

const buildRequiredSteps = (
  schema: TIntegrationSchemaDiscoveryResponse | undefined,
): string[] => {
  const required = Array.isArray(schema?.create_schema?.required)
    ? (schema?.create_schema.required as string[])
    : [];

  if (!required.length) {
    return ["integration_type"];
  }

  return required;
};

export const buildIntegrationCatalog = (
  metadataList: TIntegrationTypeMetadata[],
  schemas: TIntegrationSchemaDiscoveryResponse[],
): TIntegrationCatalogRow[] => {
  const visibleMetadata = metadataList.filter(
    (metadata) =>
      !HIDDEN_INTEGRATIONS.has(metadata.integration_type.toLowerCase()),
  );

  const schemaByType = new Map<string, TIntegrationSchemaDiscoveryResponse>(
    schemas.map((schema) => [schema.integration_type, schema]),
  );

  const rows = visibleMetadata.map((metadata) => {
    const schema = schemaByType.get(metadata.integration_type);
    const status = inferStatus(metadata);

    let disabledReason = metadata.disabled_reason ?? undefined;
    if (status === "coming_soon" && !disabledReason) {
      if (metadata.integration_type.toLowerCase().includes("gohighlevel")) {
        disabledReason = "Coming soon in Integrations V2.";
      } else {
        disabledReason =
          "Self-serve setup is not available in Integrations V2 yet. Request a custom integration.";
      }
    }

    return {
      integration_type: metadata.integration_type,
      display_name: metadata.display_name,
      description: metadata.description,
      status,
      disabled_reason: disabledReason,
      required_steps: buildRequiredSteps(schema),
      metadata,
      schema,
    };
  });

  return rows.sort((a, b) => {
    const statusOrder: Record<TIntegrationV2Status, number> = {
      ready: 0,
      existing_flow: 1,
      coming_soon: 2,
    };

    const statusDelta = statusOrder[a.status] - statusOrder[b.status];
    if (statusDelta !== 0) return statusDelta;
    return a.display_name.localeCompare(b.display_name);
  });
};
